import { useEffect, useRef } from 'react';
import { selectRoom, useAmIHost } from '@/entities/room';
import { useGameStore, useSessionStore } from '@/shared/hooks/use-shallow-store';
import { trackEvent } from '@/shared/lib/analytics';
import { LobbyRoomView } from '../ui/LobbyRoomView';

/**
 * Online lobby orchestrator. Renders the in-room roster + start button. The
 * connection banner is mounted once at the app root via
 * `ConnectionStatusProvider`, so this view no longer wires its own.
 *
 * Fires `lobby.player_seated` once per (room × identity) so dashboards can
 * count distinct players reaching the lobby — denominator for the
 * customization-rate funnel.
 */
export const OnlineLobbyOrchestrator = () => {
  const room = useGameStore(selectRoom);
  const identity = useSessionStore((s) => s.identity);
  const isHost = useAmIHost();
  const seatedKey = room && identity ? `${room.id}:${identity.playerId}` : null;
  const seatedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!seatedKey || !room || !identity) return;
    if (seatedRef.current === seatedKey) return;
    seatedRef.current = seatedKey;
    trackEvent({
      name: 'lobby.seated',
      data: { roomId: room.id, isHost, seatNumber: room.players.length },
    });
  }, [seatedKey, room, identity, isHost]);

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1">
        <LobbyRoomView />
      </div>
    </div>
  );
};
